import { useId, type ReactElement } from "react";

/**
 * Alex's default-avatar art — the assistant's product-matching face when the
 * lawyer has not chosen one (D-11, Assistant-Hub redesign S1). Three original
 * variants in the brand palette (coffee / cream / gold), gender-neutral and
 * institutional — never a real person's likeness. The operator picks one LIVE
 * on the hero during the S1 e2e; the pre-highlighted default is variant 1.
 *
 * INLINE SVG components (not `.svg` assets): LexIDE has no svg-asset loader and
 * its custom marks are all inline components (`AgentIncognitoIcon`,
 * `LexiunMarkRailIcon`). Each is a self-contained roundel — a warm circular
 * background with a simple bust — so it reads the same on any surface, light or
 * dark, at 20px (slim strip) or 48px (hero). `useId()` namespaces the
 * gradient/clip ids so several can render on one page (the S3 picker grid).
 *
 * Referenced by TOKEN in `identity.avatar` (`builtin:alex-1`, capped at 500 —
 * C1), which `AssistantAvatar` resolves back to the component here.
 */

export interface AlexAvatarProps {
  size?: number | string;
  className?: string;
  testid?: string;
}

/** Variant 1 — the warm "colleague": gold roundel, cream face, coffee bust. */
export function AlexAvatar1({ size = 48, className, testid }: AlexAvatarProps) {
  const id = useId();
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      data-testid={testid}
      role="img"
      aria-hidden="true"
    >
      <defs>
        <clipPath id={`${id}-clip`}>
          <circle cx="32" cy="32" r="32" />
        </clipPath>
        <linearGradient id={`${id}-bg`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#e2b069" />
          <stop offset="1" stopColor="#be8a3d" />
        </linearGradient>
      </defs>
      <g clipPath={`url(#${id}-clip)`}>
        <rect width="64" height="64" fill={`url(#${id}-bg)`} />
        <path d="M11 64c0-14 9.5-21 21-21s21 7 21 21Z" fill="#3b342d" />
        <circle cx="32" cy="27" r="13" fill="#f2f1ec" />
        <path
          d="M19.5 25c0-9 6-13 12.5-13s12.5 4 12.5 13c-3-6-8-8.5-12.5-8.5S22.5 19 19.5 25Z"
          fill="#3b342d"
        />
      </g>
    </svg>
  );
}

/** Variant 2 — the elegant, darker take: coffee roundel, gold ring + bust. */
export function AlexAvatar2({ size = 48, className, testid }: AlexAvatarProps) {
  const id = useId();
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      data-testid={testid}
      role="img"
      aria-hidden="true"
    >
      <defs>
        <clipPath id={`${id}-clip`}>
          <circle cx="32" cy="32" r="32" />
        </clipPath>
        <linearGradient id={`${id}-bg`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3b342d" />
          <stop offset="1" stopColor="#23201c" />
        </linearGradient>
      </defs>
      <g clipPath={`url(#${id}-clip)`}>
        <rect width="64" height="64" fill={`url(#${id}-bg)`} />
        <path d="M12 64c0-13 9-20 20-20s20 7 20 20Z" fill="#d4a052" />
        <circle cx="32" cy="27" r="13" fill="#f2f1ec" />
        <circle cx="32" cy="27" r="13" fill="none" stroke="#e2b069" strokeWidth="2" />
        <path
          d="M20 24c0-8 5.5-12 12-12s12 4 12 12c-3-5.5-7.5-7.5-12-7.5S23 18.5 20 24Z"
          fill="#23201c"
        />
      </g>
    </svg>
  );
}

/** Variant 3 — the light, minimal take: cream roundel + gold ring, coffee bust. */
export function AlexAvatar3({ size = 48, className, testid }: AlexAvatarProps) {
  const id = useId();
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      data-testid={testid}
      role="img"
      aria-hidden="true"
    >
      <defs>
        <clipPath id={`${id}-clip`}>
          <circle cx="32" cy="32" r="32" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${id}-clip)`}>
        <rect width="64" height="64" fill="#f2f1ec" />
        <circle cx="32" cy="32" r="30" fill="none" stroke="#e2b069" strokeWidth="3" />
        <path d="M14 64c0-12 8-18 18-18s18 6 18 18Z" fill="#5b524b" />
        <circle cx="32" cy="28" r="12" fill="#3b342d" />
        <path
          d="M21 27c0-8 5-11.5 11-11.5S43 19 43 27c-2.5-5-7-7-11-7s-8.5 2-11 7Z"
          fill="#23201c"
        />
      </g>
    </svg>
  );
}

export const ALEX_AVATAR_TOKENS = [
  "builtin:alex-1",
  "builtin:alex-2",
  "builtin:alex-3",
] as const;

export type AlexAvatarToken = (typeof ALEX_AVATAR_TOKENS)[number];

/** The pre-highlighted default (D-11 recommendation (a); operator may repick). */
export const ALEX_DEFAULT_AVATAR_TOKEN: AlexAvatarToken = "builtin:alex-1";

export const ALEX_AVATARS: Record<AlexAvatarToken, (props: AlexAvatarProps) => ReactElement> = {
  "builtin:alex-1": AlexAvatar1,
  "builtin:alex-2": AlexAvatar2,
  "builtin:alex-3": AlexAvatar3,
};

export function isAlexAvatarToken(v: string | null | undefined): v is AlexAvatarToken {
  return v != null && v in ALEX_AVATARS;
}
